<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Background/Testimonial';
    let nextLink= '/Background/About_the_Name';
    
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="what-is-projectional-editing-1"
  bind:intersecting={visibleById["what-is-projectional-editing-1"]}
  onIntersectChange={(visible) => setVisible("what-is-projectional-editing-1", visible)}
>What is Projectional Editing?</SectionComponent>
<p>The essential characteristic of projectional editing is that the user manipulates
the Abstract Syntax Tree (AST) directly. In contrast, the traditional manner of editing is that
a user manipulates a text-string, which is then (re)parsed into a (changed) AST.</p>
<p>For a generic view on projectional editing
see <a href="https://en.wikipedia.org/wiki/Structure*editor" target="_blank">this page</a> on Wikipedia.</p>
<SectionComponent tag="h2" 
  id="projectional-workflow-2"
  bind:intersecting={visibleById["projectional-workflow-2"]}
  onIntersectChange={(visible) => setVisible("projectional-workflow-2", visible)}
>Projectional workflow</SectionComponent>
<p>The overall flow in a projectional editor generated by Freon is shown in this figure.</p>
<Figure
imageName={'background/projection-overview.png'}
caption={'General flow of projectional editing'}
figureNumber={1}
/>
<ol>
<li>The model/AST is mapped to a visual presentation (the projection).</li>
<li>The projection is shown to the user.</li>
<li>The user performs an action on the projection.</li>
<li>The action on the projection is mapped to an action on the model/AST.</li>
<li>(or 1 again) The changed model/AST is (re)mapped to a visual representation.</li>
</ol>
<SectionComponent tag="h2" 
  id="support-for-expressions-3"
  bind:intersecting={visibleById["support-for-expressions-3"]}
  onIntersectChange={(visible) => setVisible("support-for-expressions-3", visible)}
>Support For Expressions</SectionComponent>
<p>Editing expressions in a projectional editor is a well-known challenge. The representation of an expression
in the AST is highly structured. Yet, because its appearance to the user looks textual, users expect that
the editing behavior resembles classical text editing as much as possible.</p>
<p>In <a href="https://www.voelter.de/data/pub/fse2016-projEditing.pdf" target="_blank">Efficiency
of Projectional Editing: A Controlled Experiment</a> the authors express the problem as follows.</p>
<p>Editing expressions with their fine-grained tree structure is one of the major challenges in a
projectional editor. The inability to insert and remove parentheses in arbitrary places (and
then refactor the tree structure according to the precedence expressed by the parentheses) is an
example.</p>
<p>[Markus Voelter a.o., Efficiency of Projectional Editing]</p>
<p>For this reason, Freon has in-built support for expressions. There is extra functionality to
be able to add an expression before or after an existing expression. Secondly, the AST of any binary
expression is automatically balanced.</p>
<SectionComponent tag="h2" 
  id="adding-to-an-existing-expression-4"
  bind:intersecting={visibleById["adding-to-an-existing-expression-4"]}
  onIntersectChange={(visible) => setVisible("adding-to-an-existing-expression-4", visible)}
>Adding to an Existing Expression</SectionComponent>
<Figure
imageName={'background/expressions-problem.png'}
caption={'Editing expressions'}
figureNumber={2}
/>
<p>In the above figure, when the user types a <code>+</code> symbol after the <code>**</code>, it is not enough
to simply add the <code>+</code> in the AST.</p>
<ul>
<li>First of all, a placeholder should be added to make the expression structurally correct,
so we should see something like
<ul>
<li><code>a * ... + b</code>.</li>
</ul>
</li>
<li>Doing so will lead to the second AST shown.</li>
<li>But … this AST is not balanced right!
<ul>
<li>When adding parenthesis to show the AST, this will look like <code>a * (... + b)</code></li>
<li>But the user (using their knowledge of priorities of <code>**</code> and <code>+</code>) will read <code>(a * ...) + b</code>.</li>
</ul>
</li>
<li>Therefore, the AST needs to be re-balanced to take the priorities of the multiplication and addition into account.
This re-balancing leads to the third AST shown, where the structure of the AST
properly reflects what the user understands.</li>
</ul>
<p>Freon understands this kind of tree balancing and will automatically apply it while editing expressions.</p>
<SectionComponent tag="h2" 
  id="persistence-5"
  bind:intersecting={visibleById["persistence-5"]}
  onIntersectChange={(visible) => setVisible("persistence-5", visible)}
>Persistence</SectionComponent>
<p>Typical user actions are to <em>create</em> a new element in the AST, to <em>change</em> the content of an element,
or to <em>delete</em> an element.</p>
<p>Certainly, these changes may need to be persisted. When the user triggers
the menu actions <em>save unit</em> or <em>open unit</em>, the AST is pushed to or retrieved from a server.</p>
<Figure
imageName={'background/projection-overview.png'}
caption={'General flow of projectional editing'}
figureNumber={3}
/>


<PrevNextSection {prevLink} {nextLink} />